import type { MaturityAreaKey, MaturityNodeStatus } from '@cobliteam/hooks/src/useBasicValueMaturity/basicValueMaturityModel';
import type { MaturityAreaState } from '@cobliteam/hooks/src/useBasicValueMaturity/useBasicValueMaturity';
import { useTranslation } from '@cobliteam/i18n';
import { cn } from '@cobliteam/ui/utils';
import { Check } from 'lucide-react';
import { tv } from 'tailwind-variants';
import { AREA_ICON } from '../basicValueMaturityPresentation';

interface MaturityTrackCompactProps {
  areas: MaturityAreaState[];
  /** Stage of the step currently shown in the modal — highlighted as the active node. */
  activeAreaKey?: MaturityAreaKey;
}

const dotVariants = tv({
  base: 'grid size-11 place-items-center rounded-full border-2 font-bold font-display transition-colors',
  variants: {
    status: {
      done: 'border-success-9 bg-success-9 text-white',
      current: 'border-primary-9 bg-primary-9 text-white ring-4 ring-primary-9/15',
      locked: 'border-gray-6 bg-white text-gray-9',
    },
  },
});

const TrackNode = ({ area, active }: { area: MaturityAreaState; active: boolean }) => {
  const { t } = useTranslation('basic-value-onboarding');
  const AreaIcon = AREA_ICON[area.key];
  // The active step's stage reads as "current" even if the fleet hasn't reached it yet.
  const status: MaturityNodeStatus = active ? 'current' : area.status === 'done' ? 'done' : 'locked';

  const stateText =
    status === 'done' ? t('track.state.done') : status === 'current' ? t('track.state.progress', { done: area.doneCount, total: area.totalCount }) : t('track.state.locked');

  return (
    <div className='relative z-10 flex flex-col items-center px-1.5 text-center'>
      <div className={dotVariants({ status })}>{status === 'done' ? <Check size={20} strokeWidth={3} /> : status === 'current' ? <AreaIcon size={20} /> : area.n}</div>
      <div className={cn('mt-2.5 font-display font-semibold text-sm', status === 'locked' ? 'text-gray-10' : status === 'current' ? 'text-primary-9' : 'text-gray-12')}>
        {t(`areas.${area.key}.title`)}
      </div>
      <div className={cn('mt-0.5 text-xs', status === 'current' ? 'font-semibold text-primary-9' : 'text-gray-9')}>{stateText}</div>
    </div>
  );
};

/** Stage track for the guided modal — same look as the page track, highlighting the current step's stage. */
const MaturityTrackCompact = ({ areas, activeAreaKey }: MaturityTrackCompactProps) => {
  if (areas.length === 0) return null;
  const stagesDone = areas.filter((area) => area.status === 'done').length;
  // The connector line starts/ends exactly at the first/last dot centers (`50 / count`% from each edge).
  const edge = 50 / areas.length;
  const span = 100 - 2 * edge;
  const progress = areas.length > 1 ? Math.min(stagesDone, areas.length - 1) / (areas.length - 1) : 0;

  return (
    <div className='relative grid' style={{ gridTemplateColumns: `repeat(${areas.length}, minmax(0, 1fr))` }}>
      <div className='absolute top-[22px] h-0.5 bg-gray-4' style={{ left: `${edge}%`, right: `${edge}%` }} aria-hidden />
      <div
        className='absolute top-[22px] h-0.5 bg-success-9'
        style={{ left: `${edge}%`, width: `${progress * span}%`, transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)' }}
        aria-hidden
      />
      {areas.map((area) => (
        <TrackNode key={area.key} area={area} active={area.key === activeAreaKey} />
      ))}
    </div>
  );
};

export default MaturityTrackCompact;
